import { h } from '@omni-torn/ui';
import { flattenPersonalStats } from '@omni-torn/shared';
import { battleScore, chainTotal, CHAIN_BONUS, eloBreakdown, eloChange, eloExpected, ELO_START, fairFight, MERIT_MAX_LEVEL, meritCost, meritEffect, MERITS, planMerits, respectFor, type MeritDef } from '@omni-torn/shared/torn-sims';
import { theirStatsWithMyKey } from '../player-stats';
import { torn } from '../session';
import { box, ext, num, profileUrl, table, withTableCsv } from '../ui';
import { load, numberInput, save, tile } from './calc-kit';

/**
 * Torn calculators, worked out in the browser (the formulas are in packages/shared/src/torn-sims.ts, with their sources):
 * the merit planner, the respect calculator and the Elo calculator. What you type is kept in this browser only; the Elo
 * calculator's "Mine" / "Look up" read personal stats from Torn with your own key (one call per click).
 */

const MERITS_KEY = 'omni-torn.meritPlan';
const RESPECT_KEY = 'omni-torn.respectCalc';
const ELO_KEY = 'omni-torn.eloCalc';

const levelSelect = (label: string, value: number, min = 0) =>
  h('select', { 'aria-label': label }, ...Array.from({ length: MERIT_MAX_LEVEL + 1 - min }, (_, i) => i + min).map((l) => h('option', { value: String(l), ...(l === value ? { selected: true } : {}) }, String(l)))) as HTMLSelectElement;

// ---------------------------------------------------------------- merits

interface MeritState {
  have: number | null;
  now: Record<string, number>;
  want: Record<string, number>;
}

/** The merit planner's boxes (its own page, and the Calculators page's Merits tab). */
export function meritsBody(): HTMLElement[] {
  const st = load<MeritState>(MERITS_KEY, { have: null, now: {}, want: {} });
  const tiles = h('div', { class: 'tiles' });
  const have = numberInput('Merits you have to spend', st.have ?? 0, { min: 0, id: 'merits-have' });
  if (st.have === null) have.value = '';
  const groups = h('div', {});
  const totals = () => {
    const plan = planMerits(st.now, st.want, st.have);
    tiles.replaceChildren(
      tile('This plan costs', `${num(plan.cost)} merits`),
      tile('Left to spend', plan.left === null ? '—' : num(plan.left), plan.left !== null && plan.left < 0 ? 'tile-bad' : ''),
      tile('Merits in all (planned)', num(plan.total)),
    );
    save(MERITS_KEY, st);
  };
  have.addEventListener('input', () => {
    st.have = have.value === '' ? null : Math.max(0, Math.floor(Number(have.value)) || 0);
    totals();
  });
  const row = (m: MeritDef) => {
    const now = levelSelect(`${m.name}: level now`, st.now[m.id] ?? 0);
    const want = levelSelect(`${m.name}: level wanted`, Math.max(st.now[m.id] ?? 0, st.want[m.id] ?? 0));
    const cost = h('td', { class: 'r' });
    const effect = h('td', {});
    const draw = () => {
      const n = Number(now.value);
      if (Number(want.value) < n) want.value = String(n);
      st.now[m.id] = n;
      st.want[m.id] = Number(want.value);
      const c = meritCost(n, st.want[m.id]!);
      cost.textContent = c ? num(c) : '—';
      effect.textContent = meritEffect(m, st.want[m.id]!) || '—';
      totals();
    };
    now.addEventListener('change', draw);
    want.addEventListener('change', draw);
    draw();
    return h('tr', { 'data-merit': m.id }, h('td', {}, h('strong', {}, m.name), h('div', { class: 'muted small' }, `${meritEffect(m, 1)} a level`)), h('td', {}, now), h('td', {}, want), cost, effect);
  };
  for (const g of ['Battle', 'Weapons', 'General'] as const) {
    groups.append(
      box(
        g === 'Weapons' ? 'Weapon masteries' : g,
        withTableCsv(h(
          'div',
          { class: 'table-wrap' },
          h('table', { class: 'merit-table' }, h('thead', {}, h('tr', {}, h('th', {}, 'Merit'), h('th', {}, 'Now'), h('th', {}, 'Wanted'), h('th', { class: 'r' }, 'Costs'), h('th', {}, 'Then'))), h('tbody', {}, ...MERITS.filter((m) => m.group === g).map(row))),
        )),
      ),
    );
  }
  totals();
  return [box('Your merits', [h('div', { class: 'toolbar' }, h('label', { for: 'merits-have' }, 'Merits to spend '), have), tiles], { id: 'merit-plan' }), groups];
}

export const MERITS_INTRO = 'Each upgrade has 10 levels; level n costs n merits (55 to max one). Your plan stays in this browser.';

export function renderMerits(view: HTMLElement): void {
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Merit planner'), h('p', { class: 'muted' }, MERITS_INTRO)), ...meritsBody());
}

// ---------------------------------------------------------------- respect

interface RespectState {
  level: number;
  ff: number;
  hit: number;
  war: boolean;
  retaliation: boolean;
  overseas: boolean;
  mug: boolean;
  helpers: number;
  chain: number;
}

/** The respect calculator's boxes (its own page, and the Calculators page's Respect tab). */
export function respectBody(): HTMLElement[] {
  const st = load<RespectState>(RESPECT_KEY, { level: 75, ff: 3, hit: 1, war: false, retaliation: false, overseas: false, mug: false, helpers: 0, chain: 100 });
  const level = numberInput('Target level', st.level, { min: 1, max: 100, id: 'r-level' });
  const ff = numberInput('Fair fight', st.ff, { min: 1, max: 3, step: 0.01, id: 'r-ff' });
  const hit = numberInput('Chain hit number', st.hit, { min: 1, max: 100_000, id: 'r-hit' });
  const chain = numberInput('Hits in the chain', st.chain, { min: 1, max: 100_000, id: 'r-chain' });
  const check = (id: keyof RespectState, label: string) => {
    const c = h('input', { type: 'checkbox', id: `r-${id}`, ...(st[id] ? { checked: true } : {}) }) as HTMLInputElement;
    c.addEventListener('change', () => (((st as unknown as Record<string, unknown>)[id] = c.checked), draw()));
    return h('label', { class: 'check' }, c, ` ${label}`);
  };
  const helpers = h('select', { 'aria-label': 'Faction members who joined', id: 'r-helpers' }, ...[0, 1, 2, 3, 4, 5].map((n) => h('option', { value: String(n), ...(n === st.helpers ? { selected: true } : {}) }, n ? `${n} joined` : 'Nobody joined'))) as HTMLSelectElement;
  // Fair fight from battle stat scores (yours and the target's estimate).
  const mine = numberInput('Your battle stat score', 0, { min: 0 });
  const theirs = numberInput('Target’s battle stat score', 0, { min: 0 });
  const ffNote = h('span', { class: 'muted small' });
  const fromScores = () => {
    const y = Number(mine.value);
    const t = Number(theirs.value);
    if (y > 0 && t >= 0) {
      ff.value = String(Math.round(fairFight(t, y) * 100) / 100);
      ffNote.textContent = ` → fair fight ${ff.value}`;
      draw();
    }
  };
  mine.addEventListener('input', fromScores);
  theirs.addEventListener('input', fromScores);
  const out = h('div', { class: 'respect-out', 'data-respect': '' });
  const draw = () => {
    st.level = Math.max(1, Math.min(100, Math.floor(Number(level.value)) || 1));
    st.ff = Math.max(1, Math.min(3, Number(ff.value) || 1));
    st.hit = Math.max(1, Math.min(100_000, Math.floor(Number(hit.value)) || 1));
    st.chain = Math.max(1, Math.min(100_000, Math.floor(Number(chain.value)) || 1));
    st.helpers = Number(helpers.value);
    save(RESPECT_KEY, st);
    const opts = { level: st.level, fairFight: st.ff, war: st.war, retaliation: st.retaliation, overseas: st.overseas, mug: st.mug, helpers: st.helpers };
    const r = respectFor({ ...opts, hit: st.hit });
    const c = chainTotal(opts, st.chain);
    out.replaceChildren(
      h('div', { class: 'tiles' }, h('div', { class: 'tile tile-total' }, h('div', { class: 'tile-label' }, `Respect for hit ${num(st.hit)}`), h('div', { class: 'tile-value', 'data-value': String(r.respect) }, num(r.respect, 2))), tile(`A chain of ${num(st.chain)} hits`, num(c.total, 2)), tile('…of it from bonus hits', num(c.bonus))),
      r.bonus !== null
        ? h('p', {}, `Hit ${num(st.hit)} is a bonus hit: a fixed ${num(r.bonus)} respect, nothing else counts.`)
        : h('p', { class: 'muted' }, `Base ${num(r.base, 2)} (level ${st.level})`, ...r.parts.map((p) => ` × ${num(p.x, 4)} ${p.label.toLowerCase()}`), ` = ${num(r.respect, 2)}`),
    );
  };
  for (const el of [level, ff, hit, chain]) el.addEventListener('input', draw);
  helpers.addEventListener('change', draw);
  draw();
  return [
    box('The hit', [
      h(
        'div',
        { class: 'calc-grid' },
        h('label', { for: 'r-level' }, 'Target level', level),
        h('label', { for: 'r-ff' }, 'Fair fight (1–3)', ff),
        h('label', { for: 'r-hit' }, 'Chain hit #', hit),
        h('label', { for: 'r-chain' }, 'Hits in the chain', chain),
      ),
      h('div', { class: 'toolbar' }, check('war', 'War (×2)'), check('retaliation', 'Retaliation (×1.5)'), check('overseas', 'Overseas (×1.25)'), check('mug', 'Mugged (×0.75)'), helpers),
      out,
    ], { id: 'respect' }),
    box('Fair fight from battle stats', [
      h('p', { class: 'muted small' }, 'Battle stat score = the sum of the square roots of the four stats. Fair fight = 1 + 8/3 × (target’s score ÷ yours), between 1 and 3 — 3 from about 75% of your stats.'),
      h('div', { class: 'calc-grid' }, h('label', {}, 'Your score', mine), h('label', {}, 'Target’s score', theirs)),
      ffNote,
      h('p', { class: 'muted small' }, 'Your score from your four stats: e.g. ', h('code', {}, `√1,000,000 × 4 = ${num(battleScore([1e6, 1e6, 1e6, 1e6]))}`), '.'),
    ]),
    box('Bonus hits', h('p', { class: 'muted' }, CHAIN_BONUS.map(([hitN, r]) => `${num(hitN)}: ${num(r)}`).join(' · ')), { id: 'bonus-hits' }),
    h('p', { class: 'muted small' }, 'Formulas from Torn’s wiki (Chain page): base respect, chain scale 0.25·log10(hit) + 0.75 after hit 10, war ×2, retaliation ×1.5, group attack 1.5 − 0.25·0.5^(n−1), overseas ×1.25, mug ×0.75.'),
  ];
}

export function renderRespect(view: HTMLElement): void {
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Respect calculator')), ...respectBody());
}

// ---------------------------------------------------------------- Elo

interface EloState {
  you: number;
  them: number;
}

const eloOf = (ps: Record<string, number>): number | null => ps.elo ?? ps.attacking_elo ?? null;

/** The Elo calculator's boxes (its own page, and the Calculators page's Elo tab). */
export function eloBody(): HTMLElement[] {
  const st = load<EloState>(ELO_KEY, { you: ELO_START, them: ELO_START });
  const you = numberInput('Your Elo', st.you, { min: 0, max: 10_000, id: 'elo-you' });
  const them = numberInput('Opponent’s Elo', st.them, { min: 0, max: 10_000, id: 'elo-them' });
  const status = h('span', { class: 'muted small', role: 'status', 'data-elo-status': '' });
  const who = h('input', { type: 'search', inputmode: 'numeric', placeholder: 'Opponent’s id', class: 'filter', 'aria-label': 'Opponent’s id', id: 'elo-who' }) as HTMLInputElement;
  const mine = h('button', { type: 'button', class: 'btn-small', 'data-elo-mine': '' }, 'Mine');
  const look = h('button', { type: 'button', class: 'btn-small', 'data-elo-look': '' }, 'Look up');
  const out = h('div', { 'data-elo': '' });
  const breakdown = h('div', { 'data-elo-breakdown': '' });
  const draw = () => {
    st.you = Math.max(0, Math.min(10_000, Math.round(Number(you.value)) || 0));
    st.them = Math.max(0, Math.min(10_000, Math.round(Number(them.value)) || 0));
    save(ELO_KEY, st);
    const c = eloChange(st.you, st.them);
    out.replaceChildren(
      h(
        'div',
        { class: 'tiles' },
        h('div', { class: 'tile tile-total' }, h('div', { class: 'tile-label' }, 'A win gains'), h('div', { class: 'tile-value', 'data-value': String(c.win) }, `+${c.win}`)),
        tile('A loss costs', `−${c.loss}`, c.loss > c.win ? 'tile-bad' : ''),
        tile('Your chance by Elo', `${num(eloExpected(st.you, st.them) * 100)}%`),
      ),
      h('p', { class: 'muted small' }, `Gap ${st.them - st.you >= 0 ? '+' : '−'}${num(Math.abs(st.them - st.you))}. A stalemate (or running away) changes nothing.`),
    );
    breakdown.replaceChildren(
      table(
        eloBreakdown(st.you).reverse(),
        [
          { label: 'A win gains', get: (r) => h('strong', {}, `+${r.win}`), sort: (r) => r.win, cls: 'r' },
          { label: 'Opponent’s Elo', get: (r) => (r.from === null ? `${num(r.to)} or lower` : r.to === null ? `${num(r.from)} or higher` : `${num(r.from)} – ${num(r.to)}`), sort: (r) => r.from ?? -Infinity },
          { label: 'Gap', get: (r) => (r.gapFrom === null ? `${num(r.gapTo)} or less` : r.gapTo === null ? `+${num(r.gapFrom)} or more` : `${r.gapFrom > 0 ? '+' : ''}${num(r.gapFrom)} to ${r.gapTo > 0 ? '+' : ''}${num(r.gapTo)}`) },
          { label: 'A loss costs', get: (r) => `−${r.loss}`, sort: (r) => r.loss, cls: 'r' },
        ],
        { initial: 0 },
      ),
    );
  };
  const busy = async (btn: HTMLElement, what: string, run: () => Promise<void>) => {
    btn.setAttribute('disabled', '');
    status.textContent = what;
    try {
      await run();
    } catch (err) {
      status.textContent = (err as Error).message;
    } finally {
      btn.removeAttribute('disabled');
    }
  };
  mine.addEventListener('click', () =>
    void busy(mine, 'Asking Torn…', async () => {
      const r = (await torn('/user/', { selections: 'personalstats' }, 300_000)) as { personalstats?: unknown };
      const e = eloOf(flattenPersonalStats(r.personalstats));
      if (e === null) throw new Error('Torn didn’t send your Elo.');
      you.value = String(e);
      status.textContent = `Your Elo: ${num(e)}.`;
      draw();
    }),
  );
  const lookUp = () => {
    const id = Number(who.value.replace(/\D/g, ''));
    if (!id) return;
    void busy(look, 'Asking Torn…', async () => {
      const t = await theirStatsWithMyKey(id);
      const e = eloOf(t.ps);
      if (e === null) throw new Error(`Torn didn’t send ${t.name}’s Elo.`);
      them.value = String(e);
      status.replaceChildren(ext(profileUrl(id), `${t.name} [${id}]`), `: Elo ${num(e)}.`);
      draw();
    });
  };
  look.addEventListener('click', lookUp);
  who.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') lookUp();
  });
  for (const el of [you, them]) el.addEventListener('input', draw);
  draw();
  return [
    box('Your attack Elo', [
      h('div', { class: 'calc-grid' }, h('label', { for: 'elo-you' }, 'Your Elo', you), h('label', { for: 'elo-them' }, 'Opponent’s Elo', them)),
      h('div', { class: 'toolbar' }, mine, who, look, status),
      out,
    ], { id: 'elo' }),
    box('Breakdown for your Elo', [breakdown, h('p', { class: 'muted small' }, 'What a win against each opponent brings you; the gain is the most (10) against someone 512 or more above you and nothing against someone 512 or more below.')], { id: 'elo-breakdown' }),
    h('p', { class: 'muted small' }, 'Torn’s Elo (wiki: Elo) rates attacking: everyone starts at 1,000; a win gains and a loss costs, 5 each way between equals, at most 10. It is the classic Elo formula with K = 10, rounded — it gives the wiki’s table exactly. Elo is one of your personal stats (Mine reads it with your key; Look up reads an opponent’s the same way, one call each).'),
  ];
}

export function renderElo(view: HTMLElement): void {
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Elo calculator')), ...eloBody());
}
